/**
 * Formulario de configuración de Cliente
 * Información del cliente, ONT, WiFi, paquete y datos técnicos
 * @module components/forms/ClienteConfigForm
 */

import { useState } from 'react'
import { 
  User, Phone, Mail, MapPin, Router, Wifi, 
  CreditCard, Calendar, Signal, Activity,
  Eye, EyeOff, Copy, Check
} from 'lucide-react'

// Tipos
type EstadoServicio = 'activo' | 'suspendido' | 'cortado' | 'pendiente'
type TipoServicio = 'residencial' | 'comercial' | 'empresarial' | 'corporativo'

interface ONTConfig {
  marca: string
  modelo: string
  serial: string
  mac?: string
  firmware?: string
  modoOperacion?: 'bridge' | 'router'
  fechaInstalacion?: string
}

interface WifiConfig {
  ssid24: string
  password24: string
  ssid5?: string
  password5?: string
  canal24?: number
  canal5?: number
  oculto?: boolean
  bandaSteering?: boolean
}

interface PlanConfig {
  nombre: string
  velocidadBajada: number
  velocidadSubida: number
  precio?: number
  tipoServicio: TipoServicio
  ipFija?: string
  cantidadIPs?: number
}

interface DatosTecnicos {
  potenciaRx?: number
  potenciaTx?: number
  distanciaOLT?: number
  atenuacion?: number
  vlan?: number
  pppoeUser?: string
  pppoePassword?: string
  olt?: string
  slotPuerto?: string
}

interface ClienteConfig {
  // Datos personales
  nombreCompleto: string
  documento?: string
  telefono?: string
  telefonoAlt?: string
  email?: string
  direccion: string
  referencia?: string
  
  // Datos del servicio
  numeroContrato?: string
  fechaInstalacion?: string
  fechaUltimoPago?: string
  proximoPago?: string
  estado: EstadoServicio
  
  // ONT
  ont: ONTConfig
  
  // WiFi
  wifi: WifiConfig
  
  // Plan
  plan: PlanConfig
  
  // Técnico
  tecnico: DatosTecnicos
  
  // NAP asignada
  napId?: string
  napNombre?: string
  puertoNAP?: number
  
  // Notas
  observaciones?: string
}

interface ClienteConfigFormProps {
  config?: Partial<ClienteConfig>
  onChange: (config: ClienteConfig) => void
  napsDisponibles?: Array<{ id: string; nombre: string; puertosLibres: number[] }>
}

const MARCAS_ONT = ['Huawei', 'ZTE', 'Nokia', 'Fiberhome', 'TP-Link', 'VSOL', 'C-Data', 'Otra']
const PLANES_EJEMPLO = [
  { nombre: '10 Mbps', bajada: 10, subida: 5 },
  { nombre: '20 Mbps', bajada: 20, subida: 10 },
  { nombre: '50 Mbps', bajada: 50, subida: 25 },
  { nombre: '100 Mbps', bajada: 100, subida: 50 },
  { nombre: '200 Mbps', bajada: 200, subida: 100 },
  { nombre: '500 Mbps', bajada: 500, subida: 250 },
  { nombre: '1 Gbps', bajada: 1000, subida: 500 },
]

export function ClienteConfigForm({ config, onChange, napsDisponibles = [] }: ClienteConfigFormProps) {
  const getInitialConfig = (): ClienteConfig => ({
    nombreCompleto: config?.nombreCompleto || '',
    documento: config?.documento || '',
    telefono: config?.telefono || '',
    telefonoAlt: config?.telefonoAlt || '',
    email: config?.email || '',
    direccion: config?.direccion || '',
    referencia: config?.referencia || '',
    numeroContrato: config?.numeroContrato || '',
    fechaInstalacion: config?.fechaInstalacion || '',
    fechaUltimoPago: config?.fechaUltimoPago || '',
    proximoPago: config?.proximoPago || '',
    estado: config?.estado || 'pendiente',
    ont: {
      marca: config?.ont?.marca || 'Huawei',
      modelo: config?.ont?.modelo || '',
      serial: config?.ont?.serial || '',
      mac: config?.ont?.mac || '',
      firmware: config?.ont?.firmware || '',
      modoOperacion: config?.ont?.modoOperacion || 'router',
      fechaInstalacion: config?.ont?.fechaInstalacion || '',
    },
    wifi: {
      ssid24: config?.wifi?.ssid24 || '',
      password24: config?.wifi?.password24 || '',
      ssid5: config?.wifi?.ssid5 || '',
      password5: config?.wifi?.password5 || '',
      canal24: config?.wifi?.canal24,
      canal5: config?.wifi?.canal5,
      oculto: config?.wifi?.oculto || false,
      bandaSteering: config?.wifi?.bandaSteering || false,
    },
    plan: {
      nombre: config?.plan?.nombre || '50 Mbps',
      velocidadBajada: config?.plan?.velocidadBajada || 50,
      velocidadSubida: config?.plan?.velocidadSubida || 25,
      tipoServicio: config?.plan?.tipoServicio || 'residencial',
      precio: config?.plan?.precio,
      ipFija: config?.plan?.ipFija || '',
      cantidadIPs: config?.plan?.cantidadIPs,
    },
    tecnico: {
      potenciaRx: config?.tecnico?.potenciaRx,
      potenciaTx: config?.tecnico?.potenciaTx,
      distanciaOLT: config?.tecnico?.distanciaOLT,
      atenuacion: config?.tecnico?.atenuacion,
      vlan: config?.tecnico?.vlan,
      pppoeUser: config?.tecnico?.pppoeUser || '',
      pppoePassword: config?.tecnico?.pppoePassword || '',
      olt: config?.tecnico?.olt || '',
      slotPuerto: config?.tecnico?.slotPuerto || '',
    },
    napId: config?.napId || '',
    napNombre: config?.napNombre || '',
    puertoNAP: config?.puertoNAP,
    observaciones: config?.observaciones || '',
  })
  
  const [localConfig, setLocalConfig] = useState<ClienteConfig>(getInitialConfig())

  const [showPasswords, setShowPasswords] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'personal' | 'servicio' | 'ont' | 'wifi' | 'tecnico'>('personal')

  const updateConfig = (updates: Partial<ClienteConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  const updateONT = (updates: Partial<ONTConfig>) => {
    updateConfig({ ont: { ...localConfig.ont, ...updates } })
  }

  const updateWifi = (updates: Partial<WifiConfig>) => {
    updateConfig({ wifi: { ...localConfig.wifi, ...updates } })
  }

  const updatePlan = (updates: Partial<PlanConfig>) => {
    updateConfig({ plan: { ...localConfig.plan, ...updates } })
  }

  const updateTecnico = (updates: Partial<DatosTecnicos>) => {
    updateConfig({ tecnico: { ...localConfig.tecnico, ...updates } })
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const generarPasswordWifi = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let password = ''
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return password
  }

  const aplicarPlanPredefinido = (planNombre: string) => {
    const plan = PLANES_EJEMPLO.find(p => p.nombre === planNombre)
    if (plan) {
      updatePlan({
        nombre: plan.nombre,
        velocidadBajada: plan.bajada,
        velocidadSubida: plan.subida,
      })
    }
  }

  const getEstadoColor = (estado: EstadoServicio) => {
    switch (estado) {
      case 'activo': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      case 'suspendido': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
      case 'cortado': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'pendiente': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
    }
  }

  const tabs = [
    { id: 'personal', label: 'Cliente', icon: User },
    { id: 'servicio', label: 'Servicio', icon: CreditCard },
    { id: 'ont', label: 'ONT', icon: Router },
    { id: 'wifi', label: 'WiFi', icon: Wifi },
    { id: 'tecnico', label: 'Técnico', icon: Activity },
  ] as const

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex border-b dark:border-dark-600 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Datos Personales */}
      {activeTab === 'personal' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Nombre Completo *
              </label>
              <input
                type="text"
                value={localConfig.nombreCompleto}
                onChange={(e) => updateConfig({ nombreCompleto: e.target.value })}
                placeholder="Nombre y apellidos del cliente"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Documento/ID</label>
              <input
                type="text"
                value={localConfig.documento || ''}
                onChange={(e) => updateConfig({ documento: e.target.value })}
                placeholder="DNI, Cédula, RFC..."
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Nº Contrato</label>
              <input
                type="text"
                value={localConfig.numeroContrato || ''}
                onChange={(e) => updateConfig({ numeroContrato: e.target.value })}
                placeholder="CON-001234"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Teléfono
              </label>
              <input
                type="tel"
                value={localConfig.telefono || ''}
                onChange={(e) => updateConfig({ telefono: e.target.value })}
                placeholder="+52 555 123 4567"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono Alternativo</label>
              <input
                type="tel"
                value={localConfig.telefonoAlt || ''}
                onChange={(e) => updateConfig({ telefonoAlt: e.target.value })}
                placeholder="Opcional"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                Correo Electrónico
              </label>
              <input
                type="email"
                value={localConfig.email || ''}
                onChange={(e) => updateConfig({ email: e.target.value })}
                placeholder="cliente@email.com"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Dirección *
              </label>
              <input
                type="text"
                value={localConfig.direccion}
                onChange={(e) => updateConfig({ direccion: e.target.value })}
                placeholder="Calle, número, colonia, ciudad"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Referencia</label>
              <input
                type="text"
                value={localConfig.referencia || ''}
                onChange={(e) => updateConfig({ referencia: e.target.value })}
                placeholder="Casa azul, frente al parque, etc."
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab: Servicio */}
      {activeTab === 'servicio' && (
        <div className="space-y-4">
          {/* Estado del servicio */}
          <div>
            <label className="block text-sm font-medium mb-2">Estado del Servicio</label>
            <div className="grid grid-cols-4 gap-2">
              {(['activo', 'suspendido', 'cortado', 'pendiente'] as EstadoServicio[]).map(estado => (
                <button
                  key={estado}
                  type="button"
                  onClick={() => updateConfig({ estado })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize border-2 transition-all ${
                    localConfig.estado === estado
                      ? `${getEstadoColor(estado)} border-current`
                      : 'border-gray-200 dark:border-dark-600 hover:border-gray-300'
                  }`}
                >
                  {estado === 'activo' && '🟢'}
                  {estado === 'suspendido' && '🟡'}
                  {estado === 'cortado' && '🔴'}
                  {estado === 'pendiente' && '⚪'}
                  {' '}{estado}
                </button>
              ))}
            </div>
          </div>

          {/* Plan */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <h4 className="font-medium mb-3">Plan Contratado</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan</label>
                <select
                  value={localConfig.plan.nombre}
                  onChange={(e) => aplicarPlanPredefinido(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                >
                  {PLANES_EJEMPLO.map(p => (
                    <option key={p.nombre} value={p.nombre}>{p.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Servicio</label>
                <select
                  value={localConfig.plan.tipoServicio}
                  onChange={(e) => updatePlan({ tipoServicio: e.target.value as TipoServicio })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                >
                  <option value="residencial">Residencial</option>
                  <option value="comercial">Comercial</option>
                  <option value="empresarial">Empresarial</option>
                  <option value="corporativo">Corporativo</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Velocidad Bajada (Mbps)</label>
                <input
                  type="number"
                  value={localConfig.plan.velocidadBajada}
                  onChange={(e) => updatePlan({ velocidadBajada: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Velocidad Subida (Mbps)</label>
                <input
                  type="number"
                  value={localConfig.plan.velocidadSubida}
                  onChange={(e) => updatePlan({ velocidadSubida: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precio Mensual</label>
                <input
                  type="number"
                  value={localConfig.plan.precio || ''}
                  onChange={(e) => updatePlan({ precio: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">IP Fija (si aplica)</label>
                <input
                  type="text"
                  value={localConfig.plan.ipFija || ''}
                  onChange={(e) => updatePlan({ ipFija: e.target.value })}
                  placeholder="192.168.x.x"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Instalación
              </label>
              <input
                type="date"
                value={localConfig.fechaInstalacion || ''}
                onChange={(e) => updateConfig({ fechaInstalacion: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Último Pago</label>
              <input
                type="date"
                value={localConfig.fechaUltimoPago || ''}
                onChange={(e) => updateConfig({ fechaUltimoPago: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
          </div>

          {/* NAP Asignada */}
          {napsDisponibles.length > 0 && (
            <div className="border rounded-lg p-4 dark:border-dark-600">
              <h4 className="font-medium mb-3">NAP Asignada</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">NAP</label>
                  <select
                    value={localConfig.napId || ''}
                    onChange={(e) => {
                      const nap = napsDisponibles.find(n => n.id === e.target.value)
                      updateConfig({ 
                        napId: e.target.value, 
                        napNombre: nap?.nombre,
                        puertoNAP: undefined 
                      })
                    }}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                  >
                    <option value="">Seleccionar NAP...</option>
                    {napsDisponibles.map(nap => (
                      <option key={nap.id} value={nap.id}>
                        {nap.nombre} ({nap.puertosLibres.length} puertos libres)
                      </option>
                    ))}
                  </select>
                </div>
                {localConfig.napId && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Puerto</label>
                    <select
                      value={localConfig.puertoNAP || ''}
                      onChange={(e) => updateConfig({ puertoNAP: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                    >
                      <option value="">Seleccionar puerto...</option>
                      {napsDisponibles.find(n => n.id === localConfig.napId)?.puertosLibres.map(p => (
                        <option key={p} value={p}>Puerto {p}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: ONT */}
      {activeTab === 'ont' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Marca</label>
              <select
                value={localConfig.ont.marca}
                onChange={(e) => updateONT({ marca: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              >
                {MARCAS_ONT.map(marca => (
                  <option key={marca} value={marca}>{marca}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Modelo</label>
              <input
                type="text"
                value={localConfig.ont.modelo}
                onChange={(e) => updateONT({ modelo: e.target.value })}
                placeholder="Ej: HG8145V5"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número de Serie *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={localConfig.ont.serial}
                  onChange={(e) => updateONT({ serial: e.target.value.toUpperCase() })}
                  placeholder="HWTC12345678"
                  className="flex-1 px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 font-mono"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(localConfig.ont.serial, 'serial')}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 dark:border-dark-600"
                  title="Copiar"
                >
                  {copiedField === 'serial' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">MAC Address</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={localConfig.ont.mac || ''}
                  onChange={(e) => updateONT({ mac: e.target.value.toUpperCase() })}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="flex-1 px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 font-mono"
                />
                {localConfig.ont.mac && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(localConfig.ont.mac!, 'mac')}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 dark:border-dark-600"
                    title="Copiar"
                  >
                    {copiedField === 'mac' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Firmware</label>
              <input
                type="text"
                value={localConfig.ont.firmware || ''}
                onChange={(e) => updateONT({ firmware: e.target.value })}
                placeholder="V5R020C00S125"
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Modo Operación</label>
              <select
                value={localConfig.ont.modoOperacion || 'router'}
                onChange={(e) => updateONT({ modoOperacion: e.target.value as 'bridge' | 'router' })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              >
                <option value="router">Router (NAT)</option>
                <option value="bridge">Bridge</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha Instalación ONT</label>
              <input
                type="date"
                value={localConfig.ont.fechaInstalacion || ''}
                onChange={(e) => updateONT({ fechaInstalacion: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab: WiFi */}
      {activeTab === 'wifi' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Configuración WiFi</h4>
            <button
              type="button"
              onClick={() => setShowPasswords(!showPasswords)}
              className="text-sm flex items-center gap-1 text-gray-600 hover:text-gray-800 dark:text-gray-400"
            >
              {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPasswords ? 'Ocultar' : 'Mostrar'} contraseñas
            </button>
          </div>

          {/* 2.4 GHz */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-4 h-4 text-blue-500" />
              <h5 className="font-medium">Red 2.4 GHz</h5>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre de Red (SSID)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={localConfig.wifi.ssid24}
                    onChange={(e) => updateWifi({ ssid24: e.target.value })}
                    placeholder="MiRedWiFi"
                    className="flex-1 px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(localConfig.wifi.ssid24, 'ssid24')}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 dark:border-dark-600"
                    title="Copiar"
                  >
                    {copiedField === 'ssid24' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contraseña</label>
                <div className="flex gap-2">
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={localConfig.wifi.password24}
                    onChange={(e) => updateWifi({ password24: e.target.value })}
                    placeholder="••••••••"
                    className="flex-1 px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => updateWifi({ password24: generarPasswordWifi() })}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 dark:border-dark-600 text-xs"
                    title="Generar contraseña"
                  >
                    🎲
                  </button>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(localConfig.wifi.password24, 'pass24')}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 dark:border-dark-600"
                    title="Copiar"
                  >
                    {copiedField === 'pass24' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Canal</label>
                <select
                  value={localConfig.wifi.canal24 || 'auto'}
                  onChange={(e) => updateWifi({ canal24: e.target.value === 'auto' ? undefined : parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                >
                  <option value="auto">Auto</option>
                  {[1, 6, 11].map(ch => (
                    <option key={ch} value={ch}>Canal {ch}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 5 GHz */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-4 h-4 text-purple-500" />
              <h5 className="font-medium">Red 5 GHz (Opcional)</h5>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre de Red (SSID)</label>
                <input
                  type="text"
                  value={localConfig.wifi.ssid5 || ''}
                  onChange={(e) => updateWifi({ ssid5: e.target.value })}
                  placeholder="MiRedWiFi_5G"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contraseña</label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={localConfig.wifi.password5 || ''}
                  onChange={(e) => updateWifi({ password5: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Opciones adicionales */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localConfig.wifi.oculto || false}
                onChange={(e) => updateWifi({ oculto: e.target.checked })}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-sm">Red oculta</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localConfig.wifi.bandaSteering || false}
                onChange={(e) => updateWifi({ bandaSteering: e.target.checked })}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-sm">Band Steering</span>
            </label>
          </div>
        </div>
      )}

      {/* Tab: Técnico */}
      {activeTab === 'tecnico' && (
        <div className="space-y-4">
          {/* Niveles de señal */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <div className="flex items-center gap-2 mb-3">
              <Signal className="w-4 h-4" />
              <h5 className="font-medium">Niveles de Señal</h5>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Potencia Rx (dBm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={localConfig.tecnico.potenciaRx || ''}
                  onChange={(e) => updateTecnico({ potenciaRx: parseFloat(e.target.value) || undefined })}
                  placeholder="-20.5"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
                <p className="text-xs text-gray-500 mt-1">Normal: -8 a -27 dBm</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Potencia Tx (dBm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={localConfig.tecnico.potenciaTx || ''}
                  onChange={(e) => updateTecnico({ potenciaTx: parseFloat(e.target.value) || undefined })}
                  placeholder="2.5"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Distancia al OLT (m)</label>
                <input
                  type="number"
                  value={localConfig.tecnico.distanciaOLT || ''}
                  onChange={(e) => updateTecnico({ distanciaOLT: parseInt(e.target.value) || undefined })}
                  placeholder="1500"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Atenuación Total (dB)</label>
                <input
                  type="number"
                  step="0.1"
                  value={localConfig.tecnico.atenuacion || ''}
                  onChange={(e) => updateTecnico({ atenuacion: parseFloat(e.target.value) || undefined })}
                  placeholder="18.5"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
            </div>
          </div>

          {/* Configuración de red */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <h5 className="font-medium mb-3">Configuración de Red</h5>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">VLAN</label>
                <input
                  type="number"
                  value={localConfig.tecnico.vlan || ''}
                  onChange={(e) => updateTecnico({ vlan: parseInt(e.target.value) || undefined })}
                  placeholder="100"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">OLT</label>
                <input
                  type="text"
                  value={localConfig.tecnico.olt || ''}
                  onChange={(e) => updateTecnico({ olt: e.target.value })}
                  placeholder="OLT-Central-01"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slot/Puerto OLT</label>
                <input
                  type="text"
                  value={localConfig.tecnico.slotPuerto || ''}
                  onChange={(e) => updateTecnico({ slotPuerto: e.target.value })}
                  placeholder="0/1/5"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
            </div>
          </div>

          {/* PPPoE */}
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <h5 className="font-medium mb-3">Credenciales PPPoE (si aplica)</h5>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Usuario PPPoE</label>
                <input
                  type="text"
                  value={localConfig.tecnico.pppoeUser || ''}
                  onChange={(e) => updateTecnico({ pppoeUser: e.target.value })}
                  placeholder="usuario@isp.com"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contraseña PPPoE</label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={localConfig.tecnico.pppoePassword || ''}
                  onChange={(e) => updateTecnico({ pppoePassword: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Observaciones (siempre visible) */}
      <div className="border-t pt-4 dark:border-dark-600">
        <label className="block text-sm font-medium mb-1">Observaciones</label>
        <textarea
          value={localConfig.observaciones || ''}
          onChange={(e) => updateConfig({ observaciones: e.target.value })}
          rows={2}
          placeholder="Notas adicionales sobre el cliente..."
          className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
        />
      </div>
    </div>
  )
}
